import ApiService from "../services/api";

export const fetchDtcModules = async (assetIds: string[]): Promise<{ module: string, spn: string, fmi: string }[]> => {
    const apiService = new ApiService(import.meta.env.VITE_ALERTS_URL, 'rio-maintenance');
    try {
        const response = await apiService.post<{ alerts: { spn: string, fmi: string, description: string }[] }>(
            `/faultCodes/topFaults`,
            {
                "assetIds": assetIds,
                "tagIds": [],
                "startAt": new Date(new Date().setDate(new Date().getDate() - 1)).toISOString(),
                "endAt": new Date().toISOString(),
                "pageSize": 1000,
                "page": 0,
                "search": "",
                "zone": "UTC-3"
            }
        );

        // Remove duplicates based on spn, fmi, and module (description)
        const seen = new Set();
        return (response.data?.alerts ?? [])
            .map((el: { spn: string, fmi: string, description: string }) => ({
                spn: el.spn,
                fmi: el.fmi,
                module: el.description
            }))
            .filter(item => {
                const key = `${item.spn}-${item.fmi}-${item.module}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
    } catch (error) {
        console.error(error);
        return [];
    }
}

export interface DtcAlert {
    id: string;
    dateTime: Date;
    spn: string;
    spnDescription: string;
    fmi: string;
    fmiDescription: string;
    sourceAddress: string;
    sourceAddressDescription: string;
    status: string;
    alerts?: DtcAlert[];
}

export const fetchDtcAlertsHistory = async (
    assetIds: string[],
    startAt?: Date,
    endAt?: Date,
    page: number = 0,
    search: string = '',
    status: 'all' | 'attention' | 'critical' = 'all'
): Promise<{ alerts: DtcAlert[], total: number }> => {

    let lampFilter: string[] = [];
    switch (status) {
        case 'attention':
            lampFilter = ["AMBER", "MIL"];
            break;
        case 'critical':
            lampFilter = ["RED", "PROTECTED"];
            break;
        default:
            lampFilter = [];
            break;
    }

    const apiService = new ApiService(import.meta.env.VITE_ALERTS_URL, 'rio-maintenance');
    try {
        const response = await apiService.post<{ 
            data: { date: string, description: string, fmi: string, fmiDescription: string, spn: string, spnDescription: string, sourceAddress: string, sourceAddressDescription: string, lampStatus: string }[],
            total: { total: number }
        }>(
            `/faultCodes`,
            {
                "assetIds": assetIds,
                "tagIds": [],
                "startAt": startAt ? startAt.toISOString() : new Date(new Date().setDate(new Date().getDate() - 1)).toISOString(),
                "endAt": endAt ? endAt.toISOString() : new Date().toISOString(),
                "pageSize": 1000,
                "page": page,
                "search": search,
                "zone": "UTC-3",
                "lampFilter": lampFilter,
            }
        );

        // Remove duplicates based on spn, fmi, and sourceAddress
        const seen = new Set();
        const alerts = (response.data?.data ?? [])
            .map((el: any) => ({
                id: `${el.spn}-${el.fmi}-${el.sourceAddress}`,
                dateTime: new Date(el.date),
                spn: el.spn,
                fmi: el.fmi,
                spnDescription: el.spnDescription,
                fmiDescription: el.fmiDescription,
                sourceAddress: el.sourceAddress,
                sourceAddressDescription: el.sourceAddressDescription,
                status: el.lampStatus,
                alerts: response.data?.data
                    ?.filter((alert: any) => alert.spn === el.spn && alert.fmi === el.fmi && alert.sourceAddress === el.sourceAddress)
                    .map((alert: any) => ({
                        id: `${alert.spn}-${alert.fmi}-${alert.sourceAddress}`,
                        dateTime: new Date(alert.date),
                        ...el,
                    })) ?? [],
            }))
            .filter(item => {
                const key = `${item.spn}-${item.fmi}-${item.sourceAddress}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });

        return { alerts, total: response.data?.total?.total ?? 0 };
    } catch (error) {
        console.error(error);
        return { alerts: [], total: 0 };
    }
}